// frontend/src/components/common/ImageLightbox.jsx
import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ZoomIn, ZoomOut, RotateCw, Download, ChevronLeft, ChevronRight } from 'lucide-react'
import { createPortal } from 'react-dom'

const ImageLightbox = ({ 
  isOpen, 
  onClose, 
  images = [], 
  currentIndex = 0, 
  onIndexChange,
  showNavigation = true,
  showControls = true,
  showDownload = true
}) => {
  const [zoomLevel, setZoomLevel] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [selectedIndex, setSelectedIndex] = useState(currentIndex)

  // Update selected index when currentIndex prop changes
  useEffect(() => {
    setSelectedIndex(currentIndex)
  }, [currentIndex])

  // Reset zoom and rotation when image changes
  useEffect(() => {
    setZoomLevel(1)
    setRotation(0)
  }, [selectedIndex])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return
      
      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowLeft':
          navigateImage(-1)
          break
        case 'ArrowRight':
          navigateImage(1)
          break
        case '+':
        case '=':
          handleZoom(0.2)
          break
        case '-':
          handleZoom(-0.2)
          break
        case 'r':
        case 'R':
          handleRotate()
          break
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyPress)
      // Prevent body scroll when lightbox is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      window.removeEventListener('keydown', handleKeyPress)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, selectedIndex, zoomLevel])

  const navigateImage = (direction) => {
    const newIndex = selectedIndex + direction
    if (newIndex >= 0 && newIndex < images.length) {
      setSelectedIndex(newIndex)
      onIndexChange?.(newIndex)
    }
  }

  const handleZoom = (delta) => {
    setZoomLevel(prev => Math.max(0.5, Math.min(3, prev + delta)))
  }

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360)
  }

  const downloadImage = (image) => {
    const link = document.createElement('a')
    
    // Handle different image data formats
    let imageSrc = ''
    if (image.base64 || image.full_base64) {
      imageSrc = `data:image/png;base64,${image.base64 || image.full_base64}`
    } else if (image.src || image.url) {
      imageSrc = image.src || image.url
    }
    
    link.href = imageSrc
    link.download = image.filename || image.name || `image-${selectedIndex + 1}.png`
    link.click()
  }

  const currentImage = images[selectedIndex]

  if (!isOpen || !currentImage) return null

  // Get image source from different possible formats
  const getImageSrc = (image) => {
    if (image.base64 || image.full_base64) {
      return `data:image/png;base64,${image.base64 || image.full_base64}`
    }
    return image.src || image.url || ''
  }

  const lightboxContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/95 backdrop-blur-md z-[9999] flex items-center justify-center"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={onClose}
      >
        {/* Modal Content - Full Screen */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-screen h-screen bg-black overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-30 bg-gradient-to-b from-black/80 via-black/40 to-transparent p-4 md:p-6">
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center space-x-4">
                {/* Image Title/Step Info */}
                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/10">
                  <span className="text-lg md:text-xl font-bold">
                    {currentImage.step_number ? `Step ${currentImage.step_number}` : 
                     currentImage.title || currentImage.name || `Image ${selectedIndex + 1}`}
                  </span>
                </div>
                
                {/* Action/Description */}
                {currentImage.action && (
                  <div className="bg-blue-600/80 backdrop-blur-sm rounded-xl px-4 py-2 max-w-md border border-blue-400/30">
                    <span className="text-sm md:text-base font-medium truncate">{currentImage.action}</span>
                  </div>
                )}
                
                {currentImage.description && !currentImage.action && (
                  <div className="bg-purple-600/80 backdrop-blur-sm rounded-xl px-4 py-2 max-w-md border border-purple-400/30">
                    <span className="text-sm md:text-base font-medium truncate">{currentImage.description}</span>
                  </div>
                )}
              </div>
              
              {/* Close Button */}
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-full p-3 md:p-4 transition-all duration-200 border border-white/10"
              >
                <X className="h-6 w-6 md:h-7 md:w-7" />
              </motion.button>
            </div>
          </div>

          {/* Image Container - Full Screen */}
          <div className="relative flex items-center justify-center w-full h-full overflow-hidden bg-black">
            <motion.img
              src={getImageSrc(currentImage)}
              alt={currentImage.alt || currentImage.action || `Image ${selectedIndex + 1}`}
              className="w-full h-full object-contain"
              style={{
                transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                transition: 'transform 0.3s ease',
                maxWidth: 'none',
                maxHeight: 'none'
              }}
            />

            {/* Navigation Arrows */}
            {showNavigation && images.length > 1 && (
              <>
                <motion.button
                  whileHover={{ scale: 1.1, x: -2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => navigateImage(-1)}
                  disabled={selectedIndex === 0}
                  className="absolute left-4 md:left-8 top-1/2 transform -translate-y-1/2 bg-black/60 backdrop-blur-sm hover:bg-black/80 text-white p-3 md:p-4 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 border border-white/10"
                >
                  <ChevronLeft className="h-6 w-6 md:h-8 md:w-8" />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.1, x: 2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => navigateImage(1)}
                  disabled={selectedIndex === images.length - 1}
                  className="absolute right-4 md:right-8 top-1/2 transform -translate-y-1/2 bg-black/60 backdrop-blur-sm hover:bg-black/80 text-white p-3 md:p-4 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 border border-white/10"
                >
                  <ChevronRight className="h-6 w-6 md:h-8 md:w-8" />
                </motion.button>
              </>
            )}
          </div>

          {/* Control Bar */}
          {showControls && (
            <div className="absolute bottom-0 left-0 right-0 z-30 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 md:p-6">
              <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
                {/* Image Counter */}
                {images.length > 1 && (
                  <div className="text-white bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/10">
                    <span className="text-sm md:text-base font-medium">
                      {selectedIndex + 1} of {images.length}
                    </span>
                  </div>
                )}

                {/* Controls */}
                <div className="flex items-center space-x-3">
                  {/* Zoom Controls */}
                  <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-xl p-2 border border-white/10">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleZoom(-0.2)}
                      disabled={zoomLevel <= 0.5}
                      className="text-white hover:text-blue-300 p-2 rounded-lg disabled:opacity-50 transition-colors"
                      title="Zoom out (-)"
                    >
                      <ZoomOut className="h-5 w-5" />
                    </motion.button>
                    
                    <span className="text-white text-sm font-medium min-w-[3rem] text-center">
                      {Math.round(zoomLevel * 100)}%
                    </span>
                    
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleZoom(0.2)}
                      disabled={zoomLevel >= 3}
                      className="text-white hover:text-blue-300 p-2 rounded-lg disabled:opacity-50 transition-colors"
                      title="Zoom in (+)"
                    >
                      <ZoomIn className="h-5 w-5" />
                    </motion.button>
                  </div>

                  {/* Rotate */}
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handleRotate}
                    className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-xl transition-all duration-200 border border-white/10"
                    title="Rotate (R)"
                  >
                    <RotateCw className="h-5 w-5" />
                  </motion.button>

                  {/* Download */}
                  {showDownload && (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => downloadImage(currentImage)}
                      className="bg-blue-600/80 backdrop-blur-sm hover:bg-blue-600 text-white px-4 py-3 rounded-xl transition-all duration-200 flex items-center space-x-2 border border-blue-400/30"
                      title="Download"
                    >
                      <Download className="h-5 w-5" />
                      <span className="hidden sm:inline text-sm font-medium">Download</span>
                    </motion.button>
                  )}
                </div>
              </div>

              {/* Keyboard Shortcuts Hint */}
              <div className="mt-4 text-center">
                <div className="inline-flex items-center space-x-4 text-xs text-gray-300 bg-black/40 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/10">
                  <span>ESC: Close</span>
                  {images.length > 1 && <span>←→: Navigate</span>}
                  <span>+/-: Zoom</span>
                  <span>R: Rotate</span>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )

  // Render the lightbox using a portal to ensure it appears above everything
  return createPortal(lightboxContent, document.body)
}

export default ImageLightbox